<template>
  <div
    class="sidebar-wrapper-docs full-screen hide-scrollbar"
    :class="{
      'hidden-overflow':
        $route.name === 'knowledge-case' || $route.name === 'knowledge-content',
    }"
  >
    <side-bar class="small-sidebar p-0 d-none d-lg-block">
      <template slot-scope="{}" slot="links">
        <!-- KNOWLEDGE POPOVER -->
        <div
          :class="['cursor-animation', { 'ai-feature': isAiFeature }]"
          :style="translate"
        ></div>
        <div
          :class="['bg-animation', { 'ai-feature': isAiFeature }]"
          :style="translate"
        ></div>
        <el-popover
          popper-class="navbar-popover"
          placement="right-start"
          trigger="hover"
          v-if="hasSeveralKnowledges && userAccess.knowledgeEditor"
        >
          <NavbarSubMenu
            :title="$t('dashboard-layout.knowledge')"
            :menu-options="knowledges"
            :focused-option="focusKnowledgeValue"
            @click:option="updateFocusKnowledge"
          />
          <!-- Apply router-link-active class on $route.name for /knowledge route-->
          <router-link
            class="text-center integrate-link my-1 router-link"
            :class="{ 'router-link-active': $route.name === 'knowledge-home' }"
            :to="{
              name: 'knowledge-home',
              params: { lang: editingLanguage },
            }"
            slot="reference"
          >
            <div class="icon-wrapper">
              <font-awesome-icon
                :icon="[currentRouter === 'knowledge' ? 'fas' : 'fal', 'book']"
                class="nav-icon"
              />
            </div>
          </router-link>
        </el-popover>

        <!-- KNOWLEDGE -->
        <el-tooltip
          :content="$t('dashboard-layout.knowledge')"
          placement="right"
          class="router-link"
          v-if="userAccess.knowledgeEditor && !hasSeveralKnowledges"
        >
          <router-link
            class="text-center integrate-link my-1"
            :to="{
              name: 'knowledge-home',
              params: { lang: editingLanguage },
            }"
          >
            <div class="icon-wrapper">
              <font-awesome-icon
                :icon="[currentRouter === 'knowledge' ? 'fas' : 'fal', 'book']"
                class="nav-icon"
              />
            </div>
          </router-link>
        </el-tooltip>

        <!-- DASHBOARD -->
        <el-tooltip
          :content="$t('dashboard-layout.dashboard')"
          placement="right"
          class="router-link"
          v-if="userAccess.dashboards"
        >
          <router-link class="text-center integrate-link my-1" to="/dashboard">
            <div class="icon-wrapper">
              <img
                v-if="currentRouter === 'dashboard'"
                src="~assets/chart-simple-solid.svg"
                alt="dashboard icon"
                class="nav-icon filter-solid-svg"
              />
              <img
                v-else
                src="~assets/chart-simple-light.svg"
                alt="dashboard icon"
                class="nav-icon filter-solid-svg"
              />
            </div>
          </router-link>
        </el-tooltip>

        <!-- TASKS -->
        <el-tooltip
          :content="$t('dashboard-layout.tasks')"
          placement="right"
          class="router-link"
          v-if="userAccess.tasksBoard"
        >
          <router-link class="text-center integrate-link my-1" to="/tasks">
            <div
              class="icon-wrapper d-flex align-items-center justify-content-center"
            >
              <el-badge
                v-if="backlogTasksCount > 0"
                :value="backlogTasksCount"
                :max="99"
              >
                <img
                  v-if="currentRouter === 'tasks'"
                  src="~assets/square-kanban-solid.svg"
                  alt="dashboard icon"
                  class="nav-icon filter-solid-svg"
                />
                <img
                  v-else
                  src="~assets/square-kanban-light.svg"
                  alt="dashboard icon"
                  class="nav-icon filter-solid-svg"
                />
              </el-badge>
              <div v-else class="icon-wrapper">
                <img
                  v-if="currentRouter === 'tasks'"
                  src="~assets/square-kanban-solid.svg"
                  alt="dashboard icon"
                  class="nav-icon filter-solid-svg"
                />
                <img
                  v-else
                  src="~assets/square-kanban-light.svg"
                  alt="dashboard icon"
                  class="nav-icon filter-solid-svg"
                />
              </div>
            </div>
          </router-link>
        </el-tooltip>

        <!-- NOTIFICATIONS -->
        <el-tooltip
          :content="$t('dashboard-layout.notifications')"
          placement="right"
          class="router-link"
          v-if="userAccess.notifications"
        >
          <router-link
            class="text-center integrate-link my-1"
            to="/notification-center"
          >
            <div class="icon-wrapper">
              <font-awesome-icon
                :icon="[
                  currentRouter === 'notification-center' ? 'fas' : 'fal',
                  'paper-plane',
                ]"
                class="nav-icon"
              />
            </div>
          </router-link>
        </el-tooltip>

        <!-- AUTOMATIONS -->
        <el-tooltip
          :content="$t('dashboard-layout.automations')"
          placement="right"
          class="router-link"
          v-if="userAccess.automations && hasIntegrations"
        >
          <router-link
            class="text-center integrate-link my-1"
            to="/automations"
          >
            <div class="icon-wrapper">
              <font-awesome-icon
                :icon="[
                  currentRouter === 'automations' ? 'fas' : 'fal',
                  'magic',
                ]"
                class="nav-icon"
              />
            </div>
          </router-link>
        </el-tooltip>

        <!-- ASK -->
        <el-tooltip
          :content="$t('dashboard-layout.ask')"
          placement="right"
          class="router-link"
          v-if="hasAsk"
        >
          <router-link
            class="text-center integrate-link my-1 ai-feature"
            to="/ask"
          >
            <div class="icon-wrapper">
              <font-awesome-icon
                :icon="[currentRouter === 'ask' ? 'fas' : 'fal', 'sparkles']"
                class="nav-icon ai-feature"
              />
            </div>
          </router-link>
        </el-tooltip>

        <!-- BOTTOM -->
        <div class="sidebar-bottom-group">
          <!-- SEARCH -->
          <div
            v-if="userAccess.knowledgeEditor"
            class="bottom-icon-container mb-2"
          >
            <div
              class="bottom-icon-wrapper"
              @click.stop="searchModal = !searchModal"
            >
              <font-awesome-icon
                class="bottom-icon"
                :icon="['fal', 'search']"
              />
            </div>
          </div>

          <div class="md-popover-divider mb-2"></div>

          <WebRedirection class="redirect-mayday-web mx-auto mb-2" />

          <div class="md-popover-divider mb-2"></div>

          <!-- NOTIFICATION -->
          <el-tooltip
            :content="$t('dashboard-layout.notifications-drawer')"
            placement="right"
          >
            <NotificationPopover source="admin" class="mb-2" height="80" />
          </el-tooltip>

          <!-- SETTINGS -->
          <router-link
            v-if="userAccess.settings"
            class="setting-router text-center bottom-icon-container mb-2"
            to="/settings"
          >
            <el-tooltip
              :content="$t('dashboard-layout.settings')"
              placement="right"
              class="router-link"
            >
              <div class="icon-wrapper-setting bottom-icon-wrapper">
                <font-awesome-icon
                  :icon="[currentRouter === 'settings' ? 'fas' : 'fal', 'cog']"
                  class="bottom-icon"
                />
              </div>
            </el-tooltip>
          </router-link>

          <!-- USER -->
          <UserDropdown
            :user-access="userAccess"
            @logout="logout"
            @update-language="updateAppLanguage"
          />
        </div>
      </template>
    </side-bar>

    <div class="main-content" :data="sidebarBackground">
      <div @click="toggleSidebar" class="main-content-wrapper">
        <fade-transition :duration="200" origin="center top" mode="out-in">
          <div style="max-height: calc(100vh)">
            <router-view class="d-none d-lg-block"></router-view>
            <!-- WIDEN WINDOW ALERT -->
            <div
              class="d-lg-none container-fluid widen-window d-flex align-items-center"
            >
              <div class="col-11 text-center px-4">
                <img
                  class="enlarge-gif"
                  src="~assets/material-design/empty-states/enlarge-screen.gif"
                />
                <p>
                  {{ $t('landing.enlarge-window') }}
                </p>
              </div>
            </div>
          </div>
        </fade-transition>
      </div>
    </div>
    <AdminSearchWrapper
      v-if="searchModal"
      @close="searchModal = false"
      :display="searchModal"
    />
  </div>
</template>

<script>
import AdminSearchWrapper from '@/components/SuperSearch/AdminSearchWrapper';
import NavbarSubMenu from '@/components/NavbarSubMenu';
import { mapActions, mapGetters } from 'vuex';
import NotificationPopover from '@/components/Notifications/NotificationPopover';
import WebRedirection from '@/components/Navbar/WebRedirection';
import UserDropdown from '@/components/Navbar/UserDropdown';
import { FadeTransition } from 'vue2-transitions';

export default {
  components: {
    FadeTransition,
    AdminSearchWrapper,
    NotificationPopover,
    NavbarSubMenu,
    WebRedirection,
    UserDropdown,
  },
  props: {
    autoClose: Boolean,
  },
  computed: {
    hasIntegrations() {
      return this.hasZendesk || this.hasChrome || this.hasSalesforce;
    },
    hasSeveralKnowledges() {
      return this.knowledges.length > 1;
    },
    hasAsk() {
      return this.userAccess.ask;
    },
    isAiFeature() {
      return this.aiFeatures.includes(this.currentRouter);
    },
    routerOptions() {
      return [
        ...(this.userAccess.knowledgeEditor ? ['knowledge'] : []),
        ...(this.userAccess.dashboards ? ['dashboard'] : []),
        ...(this.userAccess.tasksBoard ? ['tasks'] : []),
        ...(this.userAccess.notifications ? ['notification-center'] : []),
        ...(this.userAccess.automations && this.hasIntegrations
          ? ['automations']
          : []),
        ...(this.userAccess.ask ? ['ask'] : []),
      ];
    },
    translate() {
      const index = this.routerOptions.findIndex(
        (route) => route === this.currentRouter,
      );

      return index > -1
        ? `transform: translateY(calc(48px * ${index}));`
        : 'opacity: 0; transform: translateY(-48px);';
    },

    ...mapGetters([
      'hasZendesk',
      'hasChrome',
      'hasSalesforce',
      'userAccess',
      'isParametric',
    ]),
    ...mapGetters('knowledgeModule', [
      'knowledges',
      'focusKnowledgeValue',
      'editingLanguage',
      'focusKnowledgeDefaultLanguage',
    ]),
    ...mapGetters('taskModule', ['backlogTasksCount']),
  },
  data() {
    return {
      sidebarBackground: 'vue', //vue|blue|orange|green|red|primary
      searchModal: false,
      currentRouter: '',
      routers: [
        'knowledge-home',
        'analytics-contents',
        'board',
        'notification-center',
        'automations',
        'ask',
      ],
      aiFeatures: ['ask'],
    };
  },
  mounted() {
    this._keyListener = (e) => {
      if (
        this.userAccess.knowledgeEditor &&
        e.key === 'k' &&
        (e.metaKey || e.altKey || e.ctrlKey)
      ) {
        this.searchModal = true;
        e.preventDefault();
      } else if (e.key === 'Escape') {
        this.searchModal = false;
      }
    };
    document.addEventListener('keydown', this._keyListener.bind(this));
    this.getCompanyHostnames();
    this.getBacklogTasksCount();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._keyListener);
  },
  methods: {
    toggleSidebar() {
      if (this.$sidebar.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
    },
    updateAppLanguage(lang) {
      this.$i18n.locale = lang;
      this.$store.dispatch('updateAppLanguage', lang);
      // Set in window for web-components when rendered and eventListener for web-components already rendered
      window.___MAYDAY_CE___.locale = lang;
      window.dispatchEvent(
        new CustomEvent('md-updateAppLanguage', { detail: lang }),
      );
    },
    async updateFocusKnowledge(knowledge) {
      this.setFocusKnowledge(knowledge);
      await this.goToTranslatedEntity({
        entityType: 'Knowledge',
        lang: knowledge.defaultLanguage,
      });
    },
    ...mapActions('knowledgeModule', [
      'setFocusKnowledge',
      'goToTranslatedEntity',
    ]),
    ...mapActions('adminModule', ['getCompanyHostnames']),
    ...mapActions('taskModule', ['getBacklogTasksCount']),
    ...mapActions(['logout']),
  },
  watch: {
    $route: {
      async handler(newVal) {
        const { path, params, query, name, matched } = newVal;
        const { lang } = params;

        // Make sure there is always a lang parameter in the url
        if (!lang && matched.some(({ meta }) => meta.isLangRelated)) {
          return this.$router.replace({
            name,
            query,
            params: { ...params, lang: this.focusKnowledgeDefaultLanguage },
          });
        }

        this.currentRouter = path.split('/')[1];
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.full-screen {
  width: 100vw;
  height: 100vh;
}

.hidden-overflow {
  overflow: hidden;
}

.widen-window {
  height: 100vh;
  width: 100%;
}

.enlarge-gif {
  max-width: 50vw;
}

.nav-icon {
  z-index: 1;
  width: 20px;
  height: 20px;
  color: $grey-6-mayday;
  margin-top: 10px;
  margin-bottom: 10px;
}

.bottom-icon {
  width: 16px;
  height: 16px;
  color: $grey-6-mayday;
  &:hover {
    color: $blue-mayday;
  }
}

.sidebar-bottom-group {
  text-align: center;
  position: absolute;
  bottom: 10px;
  left: 0px;
  width: 60px;
}

.md-popover-divider {
  border-bottom: 1px solid $grey-4-mayday;
}

.small-sidebar {
  width: 60px !important;
  padding-top: 5px;
}

.integrate-link {
  display: block;
  color: #adb5bd;
  cursor: pointer;
  height: 40px;
}

.router-link-active {
  .nav-icon {
    color: $blue-mayday;
    &.ai-feature {
      color: $purple-5-mayday;
    }
  }
  .bottom-icon {
    color: $blue-mayday !important;
  }
  .filter-solid-svg {
    filter: invert(38%) sepia(90%) saturate(3765%) hue-rotate(194deg)
      brightness(100%) contrast(101%);
  }
  .icon-wrapper-setting {
    background-color: rgba($blue-mayday, 0.1);
  }

  &:hover {
    .icon-wrapper {
      background-color: transparent !important;
    }
  }
}

.sidebar-bottom-group .router-link-active::before {
  content: '';
  position: absolute;
  right: 0px;
  width: 3px;
  height: 26px;
  border-left: 2px solid $blue-mayday;
  border-radius: 2px;
}

.router-link:hover {
  align-items: center;
  display: flex;
  justify-content: center;
  .nav-icon {
    color: $blue-mayday;

    &.ai-feature {
      color: $purple-5-mayday;
    }
  }
  .bottom-icon {
    color: $blue-mayday;
  }
  .icon-wrapper {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background-color: rgba($grey-5-mayday, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 200ms ease;
  }

  .icon-wrapper-setting {
    border-radius: 4px;
    background-color: rgba($blue-mayday, 0.1);
  }
}

.cursor-animation {
  content: '';
  position: absolute;
  top: 100px;
  right: 0px;
  width: 3px;
  height: 40px;
  border-left: 2px solid $blue-mayday;
  border-radius: 2px;
  transition: transform 200ms ease 0ms;

  &.ai-feature {
    border-color: $purple-5-mayday;
  }
}

.bg-animation {
  content: '';
  position: absolute;
  top: 100px;
  right: 12px;
  width: 36px;
  height: 36px;
  margin-top: 2px;
  border-radius: 4px;
  background-color: rgba($blue-mayday, 0.1);
  transition: transform 200ms ease 0ms;
  pointer-events: none;

  &.ai-feature {
    background-color: rgba($purple-5-mayday, 0.1);
  }
}

.sidebar-button {
  max-width: 50px;
}

.setting-router {
  height: 24px;
}

.bottom-icon-wrapper {
  width: 24px;
  height: 24px;
  border-radius: 2px;
  cursor: pointer;
  &:hover {
    background-color: $grey-2-mayday;
    .bottom-icon {
      color: $blue-mayday;
    }
  }
}

.bottom-icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.multiple-knowledge-wrapper:hover {
  align-items: center;
  display: flex;
  justify-content: center;
  .icon-wrapper {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background-color: rgba($grey-5-mayday, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 200ms ease;
    .nav-icon {
      color: $blue-mayday;
    }
  }
}

.filter-solid-svg {
  filter: invert(67%) sepia(5%) saturate(1123%) hue-rotate(172deg)
    brightness(88%) contrast(87%);
}

:deep() .el-badge__content {
  background-color: $red-mayday;
  font-size: 10px;
  line-height: 14px;
}
</style>
